import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { Site } from "../types/siteTypes";

export type TenderStatus = 'draft' | 'waiting_for_retailers_reply' | 'retailers_replied' | 'under_review' | 'tender_ready' | 'sent_to_client' | 'won' | 'lost';

export interface Tender {
  id: string;
  org_id: string;
  client_id: string;
  owner_user_id: string | null;
  status: TenderStatus;
  opportunity_name: string | null;
  account_name: string | null;
  type: string | null;
  entity_name: string | null;
  abn: string | null;
  acn: string | null;
  due_at: string | null;
  current_contract_expiry: string | null;
  current_meter_provider: string | null;
  current_retailer: string | null;
  dma_provider: string | null;
  retailer_comms: string | null;
  create_dma: boolean;
  request_meter_data: boolean;
  request_bills: boolean;
  acquire_ced: boolean;
  addr_street: string | null;
  addr_city: string | null;
  addr_state: string | null;
  addr_postcode: string | null;
  addr_country: string | null;
  annual_kwh_peak: number | null;
  annual_kwh_offpeak: number | null;
  annual_kwh_shoulder: number | null;
  notes: string | null;
  sf_opportunity_id: string | null;
  sf_opportunity_stage: string | null;
  sf_quote_id: string | null;
  sf_last_sync_at: string | null;
  meter_agreement_fee?: number | null;
  extn: Record<string, any>;
  workflow_data?: Record<string, any>;
  created_at: string;
  // Retailer response tracking
  retailers_sent_count?: number;
  retailers_replied_count?: number;
  retailers_total_count?: number;
  last_email_sent_at?: string | null;
  first_reply_received_at?: string | null;
  email_stats?: {
    sent?: number;
    replied?: number;
    total?: number;
    retailers_sent?: number;
    retailers_replied?: number;
    retailers_total?: number;
  };
  // Proposal
  file_path?: string | null;
  proposal_sent_at?: string | null;
  // Joined data
  client?: {
    id: string;
    name: string;
  };
}

export interface TenderFormData {
  client_id: string;
  opportunity_name: string;
  status?: TenderStatus;
  owner_user_id?: string;
  account_name?: string;
  type?: string;
  entity_name?: string;
  notes?: string;
  // Default values for all sites
  abn?: string;
  acn?: string;
  due_at?: string;
  current_contract_expiry?: string;
  current_meter_provider?: string;
  current_retailer?: string;
  dma_provider?: string;
  retailer_comms?: string;
  create_dma?: boolean;
  request_meter_data?: boolean;
  request_bills?: boolean;
  acquire_ced?: boolean;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  addr_country?: string;
  sf_opportunity_id?: string;
  sf_tender_id?: string;
  /** Per-tender SHIELD fee override (AUD/yr). null = use sys_config default ($3,996). */
  meter_agreement_fee?: number | null;
}

export interface TenderSite {
  id: string;
  org_id: string;
  tender_id: string;
  site_id: string | null;
  name: string | null;
  nmi: string | null;
  addr_street: string | null;
  addr_city: string | null;
  addr_state: string | null;
  addr_postcode: string | null;
  addr_country: string | null;
  annual_kwh_peak: number | null;
  annual_kwh_offpeak: number | null;
  annual_kwh_shoulder: number | null;
  // Full snapshot fields
  abn: string | null;
  acn: string | null;
  due_at: string | null;
  current_contract_expiry: string | null;
  current_meter_provider: string | null;
  current_retailer: string | null;
  dma_provider: string | null;
  retailer_comms: string | null;
  create_dma: boolean | null;
  request_meter_data: boolean | null;
  request_bills: boolean | null;
  acquire_ced: boolean | null;
  extn: Record<string, any>;
  created_at: string;
}

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

/**
 * Get current user ID
 */
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.error("Error getting user ID:", error);
    return null;
  }
};

/**
 * Get all tenders for the user's organization
 */
export const getTenders = async (): Promise<{
  success: boolean;
  data?: Tender[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("tender")
      .select(`
        id,
        org_id,
        client_id,
        owner_user_id,
        status,
        opportunity_name,
        account_name,
        entity_name,
        abn,
        due_at,
        created_at,
        retailers_sent_count,
        retailers_replied_count,
        retailers_total_count,
        last_email_sent_at,
        first_reply_received_at,
        sf_opportunity_id,
        extn,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: (data || []) as unknown as Tender[] };
  } catch (error: any) {
    console.error("Error fetching tenders:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate retailer statistics for a tender
 */
export const calculateTenderRetailerStats = async (
  tenderId: string
): Promise<{
  success: boolean;
  stats?: {
    retailers_sent: number;
    retailers_replied: number;
    retailers_total: number;
    reply_rate: number;
  };
  error?: string;
}> => {
  try {
    const { data: tender, error } = await supabase
      .from('tender')
      .select('retailers_sent_count, retailers_replied_count, retailers_total_count')
      .eq('id', tenderId)
      .single();

    if (error) throw error;

    const sent = tender?.retailers_sent_count || 0;
    const replied = tender?.retailers_replied_count || 0;
    const total = tender?.retailers_total_count || 0;
    const replyRate = sent > 0 ? Math.round((replied / sent) * 100) : 0;

    return {
      success: true,
      stats: {
        retailers_sent: sent,
        retailers_replied: replied,
        retailers_total: total,
        reply_rate: replyRate
      }
    };
  } catch (error: any) {
    console.error('Error calculating retailer stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get tenders by status
 */
export const getTendersByStatus = async (
  status: TenderStatus
): Promise<{
  success: boolean;
  data?: Tender[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("tender")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching tenders by status:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get tenders by client ID
 */
export const getTendersByClientId = async (
  clientId: string
): Promise<{
  success: boolean;
  data?: Tender[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("tender")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching tenders by client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new tender
 */
export const createTender = async (
  formData: TenderFormData
): Promise<{
  success: boolean;
  data?: Tender;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from("tender")
      .insert({
        org_id: orgId,
        client_id: formData.client_id,
        owner_user_id: formData.owner_user_id || userId,
        status: formData.status || 'draft',
        opportunity_name: formData.opportunity_name,
        account_name: formData.account_name || null,
        type: formData.type || null,
        entity_name: formData.entity_name || null,
        notes: formData.notes || null,
        // Default values
        abn: formData.abn || null,
        acn: formData.acn || null,
        due_at: formData.due_at || null,
        current_contract_expiry: formData.current_contract_expiry || null,
        current_meter_provider: formData.current_meter_provider || null,
        current_retailer: formData.current_retailer || null,
        dma_provider: formData.dma_provider || null,
        retailer_comms: formData.retailer_comms || null,
        create_dma: formData.create_dma || false,
        request_meter_data: formData.request_meter_data || false,
        request_bills: formData.request_bills || false,
        acquire_ced: formData.acquire_ced || false,
        addr_street: formData.addr_street || null,
        addr_city: formData.addr_city || null,
        addr_state: formData.addr_state || null,
        addr_postcode: formData.addr_postcode || null,
        addr_country: formData.addr_country || null,
        sf_opportunity_id: formData.sf_opportunity_id || null,
        meter_agreement_fee: formData.meter_agreement_fee ?? null,
        extn: {},
        created_at: new Date().toISOString(),
      })
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error creating tender:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create tender-site junction records with full snapshot
 */
export const createTenderSites = async (data: {
  tender_id: string;
  org_id: string;
  tender_defaults?: {
    abn?: string;
    acn?: string;
    due_at?: string;
    current_contract_expiry?: string;
    current_meter_provider?: string;
    current_retailer?: string;
    dma_provider?: string;
    retailer_comms?: string;
    create_dma?: boolean;
    request_meter_data?: boolean;
    request_bills?: boolean;
    acquire_ced?: boolean;
  };
  sites: Array<{
    site_id: string;
    name: string;
    nmi: string;
    address: {
      street: string;
      city: string;
      state: string;
      postcode: string;
      country: string;
    };
    annualUsage?: {
      peak?: number;
      offPeak?: number;
      shoulder?: number;
    };
    // Site-specific overrides (optional)
    abn?: string;
    acn?: string;
    due_at?: string;
    current_contract_expiry?: string;
    current_meter_provider?: string;
    current_retailer?: string;
    dma_provider?: string;
    retailer_comms?: string;
    create_dma?: boolean;
    request_meter_data?: boolean;
    request_bills?: boolean;
    acquire_ced?: boolean;
    peak_demand_kw?: number;
    current_contract_rates?: Record<string, unknown>;
    daily_charges?: number;
    non_energy_charges?: number;
  }>;
}): Promise<{
  success: boolean;
  count?: number;
  data?: TenderSite[];
  error?: string;
}> => {
  try {
    const tenderSites = data.sites.map(site => ({
      org_id: data.org_id,
      tender_id: data.tender_id,
      site_id: site.site_id,
      name: site.name,
      nmi: site.nmi,
      addr_street: site.address.street,
      addr_city: site.address.city,
      addr_state: site.address.state,
      addr_postcode: site.address.postcode,
      addr_country: site.address.country,
      annual_kwh_peak: site.annualUsage?.peak || null,
      annual_kwh_offpeak: site.annualUsage?.offPeak || null,
      annual_kwh_shoulder: site.annualUsage?.shoulder || null,
      // Full snapshot: use site value OR tender default
      abn: site.abn || data.tender_defaults?.abn || null,
      acn: site.acn || data.tender_defaults?.acn || null,
      due_at: site.due_at || data.tender_defaults?.due_at || null,
      current_contract_expiry: site.current_contract_expiry || data.tender_defaults?.current_contract_expiry || null,
      current_meter_provider: site.current_meter_provider || data.tender_defaults?.current_meter_provider || null,
      current_retailer: site.current_retailer || data.tender_defaults?.current_retailer || null,
      dma_provider: site.dma_provider || data.tender_defaults?.dma_provider || null,
      retailer_comms: site.retailer_comms || data.tender_defaults?.retailer_comms || null,
      create_dma: site.create_dma !== undefined ? site.create_dma : (data.tender_defaults?.create_dma || null),
      request_meter_data: site.request_meter_data !== undefined ? site.request_meter_data : (data.tender_defaults?.request_meter_data || null),
      request_bills: site.request_bills !== undefined ? site.request_bills : (data.tender_defaults?.request_bills || null),
      acquire_ced: site.acquire_ced !== undefined ? site.acquire_ced : (data.tender_defaults?.acquire_ced || null),
      peak_demand_kw: site.peak_demand_kw || 0,
      current_contract_rates: site.current_contract_rates || {},
      daily_charges: site.daily_charges || 0,
      non_energy_charges: site.non_energy_charges || 0,
      extn: {},
      created_at: new Date().toISOString(),
    }));

    const { data: insertedData, error } = await supabase
      .from("tender_site")
      .insert(tenderSites)
      .select();

    if (error) throw error;

    return { success: true, count: insertedData?.length || 0, data: insertedData || [] };
  } catch (error: any) {
    console.error("Error creating tender sites:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Sync tender sites (Upsert)
 * Updates existing sites and inserts new ones.
 */
export const syncTenderSites = async (data: {
  tender_id: string;
  org_id: string;
  tender_defaults?: {
    abn?: string;
    acn?: string;
    due_at?: string;
    current_contract_expiry?: string;
    current_meter_provider?: string;
    current_retailer?: string;
    dma_provider?: string;
    retailer_comms?: string;
    create_dma?: boolean;
    request_meter_data?: boolean;
    request_bills?: boolean;
    acquire_ced?: boolean;
  };
  sites: Array<{
    id?: string; // Optional for new sites
    site_id: string;
    name: string;
    nmi: string;
    address: {
      street: string;
      city: string;
      state: string;
      postcode: string;
      country: string;
    };
    annualUsage?: {
      peak?: number;
      offPeak?: number;
      shoulder?: number;
    };
    // Site-specific overrides
    abn?: string;
    acn?: string;
    due_at?: string;
    current_contract_expiry?: string;
    current_meter_provider?: string;
    current_retailer?: string;
    dma_provider?: string;
    retailer_comms?: string;
    create_dma?: boolean;
    request_meter_data?: boolean;
    request_bills?: boolean;
    acquire_ced?: boolean;
    peak_demand_kw?: number;
    current_contract_rates?: Record<string, unknown>;
    daily_charges?: number;
    non_energy_charges?: number;
  }>;
}): Promise<{
  success: boolean;
  count?: number;
  data?: TenderSite[];
  error?: string;
}> => {
  try {
    const upsertData = data.sites.map(site => ({
      id: site.id, // If present, Supabase will try to update. If not, it might fail if not handled correctly.
      // Actually, for upsert without ID, we need a unique constraint. 
      // But here we might have mixed new and old.
      // Better strategy: Split into insert and update.
      org_id: data.org_id,
      tender_id: data.tender_id,
      site_id: site.site_id,
      name: site.name,
      nmi: site.nmi,
      addr_street: site.address.street,
      addr_city: site.address.city,
      addr_state: site.address.state,
      addr_postcode: site.address.postcode,
      addr_country: site.address.country,
      annual_kwh_peak: site.annualUsage?.peak || null,
      annual_kwh_offpeak: site.annualUsage?.offPeak || null,
      annual_kwh_shoulder: site.annualUsage?.shoulder || null,
      // Full snapshot: use site value OR tender default
      abn: site.abn || data.tender_defaults?.abn || null,
      acn: site.acn || data.tender_defaults?.acn || null,
      due_at: site.due_at || data.tender_defaults?.due_at || null,
      current_contract_expiry: site.current_contract_expiry || data.tender_defaults?.current_contract_expiry || null,
      current_meter_provider: site.current_meter_provider || data.tender_defaults?.current_meter_provider || null,
      current_retailer: site.current_retailer || data.tender_defaults?.current_retailer || null,
      dma_provider: site.dma_provider || data.tender_defaults?.dma_provider || null,
      retailer_comms: site.retailer_comms || data.tender_defaults?.retailer_comms || null,
      create_dma: site.create_dma !== undefined ? site.create_dma : (data.tender_defaults?.create_dma || null),
      request_meter_data: site.request_meter_data !== undefined ? site.request_meter_data : (data.tender_defaults?.request_meter_data || null),
      request_bills: site.request_bills !== undefined ? site.request_bills : (data.tender_defaults?.request_bills || null),
      acquire_ced: site.acquire_ced !== undefined ? site.acquire_ced : (data.tender_defaults?.acquire_ced || null),
      peak_demand_kw: site.peak_demand_kw || 0,
      current_contract_rates: site.current_contract_rates || {},
      daily_charges: site.daily_charges || 0,
      non_energy_charges: site.non_energy_charges || 0,
      extn: {},
      updated_at: new Date().toISOString(),
    }));

    // Sites carrying an explicit id from the frontend are updated directly by id.
    // Everything else is upserted in one bulk call keyed on the (tender_id, nmi)
    // unique constraint (migration 049) — this replaces the old "SELECT existing,
    // then INSERT if missing" dance, which raced under concurrent saves and could
    // create duplicate tender_site rows for the same site.
    const updates = upsertData.filter(s => s.id);
    const toUpsert = upsertData.filter(s => !s.id).map(({ id, ...rest }) => rest);

    console.log(`📊 syncTenderSites: ${updates.length} updates, ${toUpsert.length} upserts`);

    const results: TenderSite[] = [];

    // Perform Updates (explicit id from frontend)
    if (updates.length > 0) {
      for (const update of updates) {
        const { data: updated, error } = await supabase
          .from("tender_site")
          .update(update)
          .eq('id', update.id)
          .select()
          .single();

        if (error) {
          console.error(`Failed to update site ${update.id}:`, error);
        } else if (updated) {
          results.push(updated);
        }
      }
    }

    // Perform upsert (new sites, or existing sites re-synced without a frontend id)
    if (toUpsert.length > 0) {
      const { data: upserted, error } = await supabase
        .from("tender_site")
        .upsert(toUpsert, { onConflict: 'tender_id,nmi' })
        .select();

      if (error) throw error;
      if (upserted) results.push(...upserted);
    }

    return { success: true, count: results.length, data: results };
  } catch (error: any) {
    console.error("Error syncing tender sites:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing tender
 */
export const updateTender = async (
  id: string,
  formData: Partial<TenderFormData>
): Promise<{
  success: boolean;
  data?: Tender;
  error?: string;
}> => {
  try {
    const updateData: any = {};

    if (formData.opportunity_name !== undefined) updateData.opportunity_name = formData.opportunity_name;
    if (formData.status !== undefined) updateData.status = formData.status;
    if (formData.account_name !== undefined) updateData.account_name = formData.account_name;
    if (formData.type !== undefined) updateData.type = formData.type;
    if (formData.notes !== undefined) updateData.notes = formData.notes;
    if (formData.entity_name !== undefined) updateData.entity_name = formData.entity_name;
    if (formData.abn !== undefined) updateData.abn = formData.abn || null;
    if (formData.acn !== undefined) updateData.acn = formData.acn || null;
    if (formData.due_at !== undefined) updateData.due_at = formData.due_at || null;
    if (formData.current_contract_expiry !== undefined) updateData.current_contract_expiry = formData.current_contract_expiry || null;
    if (formData.current_meter_provider !== undefined) updateData.current_meter_provider = formData.current_meter_provider || null;
    if (formData.current_retailer !== undefined) updateData.current_retailer = formData.current_retailer || null;
    if (formData.dma_provider !== undefined) updateData.dma_provider = formData.dma_provider || null;
    if (formData.retailer_comms !== undefined) updateData.retailer_comms = formData.retailer_comms || null;
    if (formData.create_dma !== undefined) updateData.create_dma = formData.create_dma;
    if (formData.request_meter_data !== undefined) updateData.request_meter_data = formData.request_meter_data;
    if (formData.request_bills !== undefined) updateData.request_bills = formData.request_bills;
    if (formData.acquire_ced !== undefined) updateData.acquire_ced = formData.acquire_ced;
    if (formData.addr_street !== undefined) updateData.addr_street = formData.addr_street || null;
    if (formData.addr_city !== undefined) updateData.addr_city = formData.addr_city || null;
    if (formData.addr_state !== undefined) updateData.addr_state = formData.addr_state || null;
    if (formData.addr_postcode !== undefined) updateData.addr_postcode = formData.addr_postcode || null;
    if (formData.addr_country !== undefined) updateData.addr_country = formData.addr_country || null;
    if (formData.sf_opportunity_id !== undefined) updateData.sf_opportunity_id = formData.sf_opportunity_id || null;
    if (formData.sf_tender_id !== undefined) updateData.sf_tender_id = formData.sf_tender_id || null;
    if (formData.meter_agreement_fee !== undefined) updateData.meter_agreement_fee = formData.meter_agreement_fee ?? null;

    const { data, error } = await supabase
      .from("tender")
      .update(updateData)
      .eq("id", id)
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating tender:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a tender
 */
export const deleteTender = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("tender").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting tender:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get tender by ID
 */
export const getTenderById = async (
  id: string
): Promise<{
  success: boolean;
  data?: Tender;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("tender")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching tender:", error);
    return { success: false, error: error.message };
  }
};

export const getTenderWithSites = async (
  id: string
): Promise<{
  success: boolean;
  tender?: Tender;
  sites?: TenderSite[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("tender")
      .select(`
        *,
        client:client_id (
          id,
          name,
          abn
        ),
        tender_site (*)
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    const { tender_site: sites, ...tender } = data as any;
    return { success: true, tender, sites: sites || [] };
  } catch (error: any) {
    console.error("Error fetching tender with sites:", error);
    return { success: false, error: error.message };
  }
};


export type TenderProposalManifestEntry = {
  siteId: string | null;
  siteName: string | null;
  filePath: string;
  mode: 'combined' | 'separate';
  generatedAt: string;
};

/**
 * List every generated document for a tender (tender_proposal_document table) —
 * one row for the Combined document, or one per site for Separate mode. Returns
 * an empty list (success: true) if nothing has been generated yet.
 */
export const getTenderProposalManifest = async (
  tenderId: string
): Promise<{
  success: boolean;
  data: TenderProposalManifestEntry[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('tender_proposal_document')
      .select('site_id, site_name, file_path, mode, generated_at')
      .eq('tender_id', tenderId)
      .order('generated_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: (data || []).map((row: any) => ({
        siteId: row.site_id,
        siteName: row.site_name,
        filePath: row.file_path,
        mode: row.mode,
        generatedAt: row.generated_at,
      })),
    };
  } catch (error: any) {
    console.error("Error fetching tender proposal documents:", error);
    return { success: true, data: [] };
  }
};

/**
 * Get historical offers by postcode
 */
export const getHistoricalOffers = async (
  postcode: string,
  limit: number = 5,
  termMonths?: number,
  minUsage?: number,
  maxUsage?: number
): Promise<{
  success: boolean;
  data?: any[];
  error?: string;
}> => {
  try {
    let url = `/api/scopingai/market/historical-offers?postcode=${postcode}&limit=${limit}`;
    if (termMonths) {
      url += `&termMonths=${termMonths}`;
    }
    if (minUsage !== undefined) {
      url += `&minUsage=${minUsage}`;
    }
    if (maxUsage !== undefined) {
      url += `&maxUsage=${maxUsage}`;
    }

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch historical offers');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching historical offers:", error);
    return { success: false, error: error.message };
  }
};
